/**
 * The call log's saved views.
 *
 * Same idea as the mail folders: there is no folder column on a call, only a
 * status and two attachment flags, and each entry here is a query over them.
 *
 * React-free, and its own file because `CallsSection` reads `CALL_FILTERS` to
 * build the request and to validate a folder name off the query string - it has
 * no business importing a rail full of accordions to do that.
 */
import {
  Check,
  FileText,
  Mic,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Undo2,
  User,
  Voicemail,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { CallListParams } from "../../../../lib/calls";

export type CallFolder =
  | "all"
  | "mine"
  | "inbound"
  | "outbound"
  | "answered"
  | "missed"
  | "callbacks"
  | "voicemail"
  | "recorded"
  | "transcribed";

export const CALL_FILTERS: Record<CallFolder, CallListParams> = {
  all: {},
  // The literal word, not the viewer's id: the server reads "me" off the
  // session, so this folder keeps meaning "mine" to whoever opens it rather
  // than becoming a link to the sender's calls once it has been shared.
  mine: { operator: "me" },
  inbound: { direction: "inbound" },
  outbound: { direction: "outbound" },
  answered: { status: "completed" },
  // Both, because "busy" is a missed call everywhere else in this section - the
  // row tile and the totals strip have always counted it - and leaving it out
  // here made the folder disagree with the number printed above it.
  missed: { status: "no-answer,busy" },
  // The only folder here that is a WORK LIST rather than a view. Everything
  // else answers "show me calls like this"; this one answers "what is left to
  // do", and it empties as the work is done. One server-side switch rather than
  // a direction plus a status, because the predicate has an OR in it - see
  // `lib/callback.ts` on the API side, which is where it is written down once
  // so the number on the folder and the folder cannot disagree.
  callbacks: { needsCallback: true },
  voicemail: { voicemail: true },
  // "stored" and not "anything with a recordingSid": a call whose recording is
  // still downloading, failed, or was purged has no audio to open, and a folder
  // whose rows have nothing to play is a list of disappointments.
  recorded: { recordingStatus: "stored" },
  transcribed: { transcriptStatus: "done" },
};

// `answered` used to carry PhoneOutgoing, which is now the direction view's own
// icon. Two folders drawing the same arrow would read as two directions, so the
// status view gets a tick: it is about the outcome, not about which way the
// call went.
export const FOLDER_ICON: Record<CallFolder, LucideIcon> = {
  all: PhoneCall,
  mine: User,
  inbound: PhoneIncoming,
  outbound: PhoneOutgoing,
  answered: Check,
  missed: PhoneMissed,
  // An arrow going back, not another handset: the folder is not a kind of call,
  // it is the thing still owed to the people in it.
  callbacks: Undo2,
  voicemail: Voicemail,
  recorded: Mic,
  transcribed: FileText,
};

export const FOLDER_ORDER: CallFolder[] = [
  "all",
  // Directly under "All", because it is the same list narrowed to one person
  // rather than a kind of call. Putting it among the outcome views would file
  // "who made it" as if it were an answer to "how did it end".
  "mine",
  // The two directions sit together, before the outcome views: "which way did
  // it go" and "how did it end" are different questions and the rail should not
  // interleave them.
  "inbound",
  "outbound",
  "answered",
  "missed",
  // Right after "missed", and before the voicemail folder, because it is the
  // narrower reading of the same word: "Missed" is every unanswered call in the
  // log including your own outgoing dials, this is the ones somebody still owes
  // a call back.
  "callbacks",
  // A voicemail is the missed call that left something behind, so it sits with
  // the other two rather than off among the attachments.
  "voicemail",
  "recorded",
  "transcribed",
];
